// v2/frontend/src/stores/authStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import api, { registerTokenRefresher, refreshAccessToken as refreshSharedAccessToken } from '../services/api';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
  initialize: () => void;
}

type AuthStore = AuthState & AuthActions;

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, accessToken, refreshToken } = response.data.data;

          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Set token for API calls
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          return { success: true };
        } catch (error: unknown) {
          const message = axios.isAxiosError(error)
            ? error.response?.data?.message || 'Échec de la connexion'
            : 'Échec de la connexion';
          set({ isLoading: false, error: message });
          return { success: false, error: message };
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          if (refreshToken) {
            await api.post('/auth/logout', { refreshToken });
          }
        } catch (error) {
          // Ignore logout errors
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            error: null,
          });
          delete api.defaults.headers.common['Authorization'];
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;

        try {
          // Mutualisé avec l'intercepteur axios : un seul POST /auth/refresh à
          // la fois, et le store est mis à jour via le pont enregistré plus bas.
          await refreshSharedAccessToken();
          return true;
        } catch (error) {
          // Refresh failed, logout user
          get().logout();
          return false;
        }
      },

      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      clearError: () => set({ error: null }),

      // Initialize auth on app load
      initialize: () => {
        const { accessToken, isAuthenticated } = get();
        if (accessToken && isAuthenticated) {
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        }
      },
    }),
    {
      name: 'veloce-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Pont pour l'intercepteur axios (api.ts ne peut pas importer ce store sans
// créer un cycle) : le jeton est lu ici, et un rafraîchissement réussi met à
// jour le store — dont la persistance réécrit localStorage — plutôt que
// localStorage seul (sinon le store gardait l'ancien jeton et le réécrivait).
registerTokenRefresher({
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  onAccessTokenRefreshed: (accessToken) => useAuthStore.setState({ accessToken }),
  onRefreshFailed: () =>
    useAuthStore.setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      error: null,
    }),
});

export default useAuthStore;
